// components/artifacts/renderers/CodeArtifactRenderer.tsx
import React from "react";
import { CodeArtifact } from "@/types/Artifact";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Check, Clipboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UseTheme } from "@/components/ThemeProvider";
import { copyToClipboard } from "@/lib/artifact-utils";

interface CodeArtifactRendererProps {
  artifact: CodeArtifact;
}

export const CodeArtifactRenderer: React.FC<CodeArtifactRendererProps> = React.memo(({ 
  artifact 
}) => {
  const { theme } = UseTheme();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await copyToClipboard(artifact.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header with actions */}
      <div className="flex items-center justify-between border-b bg-sidebar px-4 py-2 text-sm dark:bg-[#21252b] dark:text-gray-300">
        <span className="font-mono">{artifact.language || 'text'}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleCopy}
            aria-label="Copy code"
          >
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Clipboard className="h-4 w-4" />}
          </Button>
          {/* <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleDownload}
            aria-label="Download code"
          >
            <Download className="h-4 w-4" />
          </Button> */}
        </div>
      </div>

      {/* Code content with proper overflow handling */}
      <div className="flex-1 overflow-auto">
        <SyntaxHighlighter
          language={artifact.language || 'text'}
          style={theme === "dark" ? oneDark : oneLight}
          customStyle={{
            margin: 0,
            padding: "1rem",
            fontSize: "0.9rem",
            lineHeight: "1.5",
            background: "transparent",
            overflow: "visible", // Let parent handle overflow
            minHeight: "100%",
          }}
          wrapLines={false} // Don't wrap lines to prevent performance issues
          wrapLongLines={true} // But do wrap long lines for readability
          showLineNumbers={true}
          lineNumberStyle={{
            minWidth: "3em",
            paddingRight: "1em",
            color: theme === "dark" ? "#5c6370" : "#90a4ae",
            userSelect: "none",
          }}
        >
          {artifact.content}
        </SyntaxHighlighter>
      </div>
    </div>
  );
});